import React, { useState, useEffect, useMemo } from "react";
import styles from "../store/index.module.css";
import { getLoggedinUser, useGetProduct, useUpdateCart } from "../../lib/util";
import { useFormatCurrency } from "../common/format-currency";
import toast from "react-hot-toast";
import useDebounce from "../common/useDebounce";
import { getCartSource, updateCart } from "../slice/cart-slice";
import { useAppDispatch, useAppSelector } from "../slice/hooks";
import { getActiveUser } from "../slice/auth-slice";
import { useReachOutBox } from "../chat/useReachOutBox";
import { useProductPriceDisplay } from "./useProductPriceDisplay";
import Carousel from "../carousel/carousel";
import ChatWindow from "../chat/ChatWindow";
import { useQueryClient } from "@tanstack/react-query";
import { useToken } from "../../lib/context/auth-context";

const ImageGallery = ({ media = [], enablePreview = true }) => {
  const hasMedia = Array.isArray(media) && media.length > 0;

  if (!hasMedia) {
    return <EmptyProductImage />;
  }

  return (
    <div className="product-details-carousel-wrapper">
      <Carousel
        media={media}
        enablePreview={enablePreview}
        imgRes={"600"}
        skeletonHeight="500px"
        zoomImgRes={"1200"}
        productCarousel={true}
      />
    </div>
  );
};

const EmptyProductImage = () => {
  return (
    <div
      style={{
        width: "42rem",
        height: "100%",
        minHeight: 360, // matches typical product carousel height
        background: "#efefef",
        borderRadius: 12,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: "#1c1c1c",
        gap: "12px",
      }}
    >
      {/* Icon */}
      <svg
        width="60"
        height="60"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.6"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>

      {/* Text */}
      <div style={{ fontSize: "1.3rem" }}>
        No image available for this product.
      </div>
    </div>
  );
};

const ProductDetails = ({
  product: initialProduct,
  cart,
  showCart,
  enablePreview = true,
  accessPoint,
  organisation
}) => {
  const { formatCurrency } = useFormatCurrency();
  const getInitialProduct = (p) => {
    if (!p.isVariant) return p;
    if (p.isVariant && p.node === "CHILD") return p;
    if (p.variants?.length > 0) return p.variants[0];
    return p;
  };

  const disableStoreProductPrice =
    accessPoint?.settings?.disableStoreFrontPrice || false;

  const dispatch = useAppDispatch();

  const { tokenData } = useAppSelector(getActiveUser);
  const cartSource = useAppSelector(getCartSource);

  const { token: invalidateToken } = useToken();

  const [parent, setParent] = useState(initialProduct);
  const [product, setProduct] = useState(getInitialProduct(initialProduct));
  const [getProductFromApi, setGetProductFromApi] = useState(false);
  const [variantData, setVariantData] = useState([]);
  const [initialCartUpdate, setInitialCartUpdate] = useState(false);
  const [error, setError] = useState("");
  const [isLoadingDummy, setLoadingDummy] = useState(false);
  const [foundMatch, setFoundMatch] = useState(true);
  const [selectedVariantColor, setSelectedVariantColor] = useState(null);
  const [selectedVariantSize, setSelectedVariantSize] = useState(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const { loggedInUser: productloggedInUser } = useAppSelector((state) =>
    getActiveUser(state, getLoggedinUser()),
  );
  const queryClient = useQueryClient();

  const cartKey = useMemo(() => {
    if (!cart?.profileId || !cart?.accessPointId) return null;
    return `${cart.profileId}:${cart.accessPointId}`;
  }, [cart?.profileId, cart?.accessPointId]);

  const [cartItems, setCartItems] = useState({
    ...{ paymentItem: [] },
    ...cart,
  });

  const [dummyCartItems, setDummyCartItems] = useState({
    ...{ paymentItem: [] },
  });

  const debouncedCartItems = useDebounce(cartItems, 1000);

  const { productPrice, selectedPricePayload } = useProductPriceDisplay(
    accessPoint,
    product,
  );

  const {
    openChatWindow,
    chatInfo,
    chatPayload,
    loggedInUser,
    openChat,
    closeChat,
    markCommentsAsRead,
    setActiveProduct,
    clearPresetMessage,
    presetMessage,
    activateGuestMode,
    chatGuestMode,
    chatBoxFetchError,
    handleBoxRetry,
    showSenderName,
  } = useReachOutBox(organisation, accessPoint);

  const { isLoadingProduct, mutate: mutateGetProduct } = useGetProduct({
    onError: (response) => {
      setError(response.message);
    },
    onSuccess: async (response) => {
      setGetProductFromApi(true);
      setParent(response.data.product);
    },
  });

  const getVariantData = (product) => {
    if (!product?.isVariant) return [];

    const { hasColour, hasSize } = product?.variant || {};
    const variants = product?.variants || [];

    return variants.map((variant) => {
      const result = {};
      if (hasColour) {
        result.colorName = variant.variant?.colorName;
        result.colorCode = variant.variant?.colorCode;
      }
      if (hasSize) {
        result.size = variant.variant?.size;
      }
      return result;
    });
  };

  const onMessage = () => {
    const chatProduct = {
      ...product,
      amount: disableStoreProductPrice ? "Negotiable Price" : productPrice,
    };
    setActiveProduct(chatProduct);
    openChat();
  };

  const findVariant = (colorName, size) => {
    if (!parent?.variants?.length) return null;
    for (const v of parent.variants) {
      const variant = v?.variant;
      const matchColor =
        uniqueColors.length > 0 && parent?.variant?.hasColour
          ? colorName
            ? variant?.colorName === colorName
            : false
          : true;
      const matchSize =
        uniqueSizes.length > 0 && parent?.variant?.hasSize
          ? size
            ? variant?.size === size
            : false
          : true;
      if (matchColor && matchSize) {
        return v;
      }
    }
    return null;
  };

  const handlePickColour = (colorName) => {
    setSelectedVariantColor(colorName);
    const variant = findVariant(colorName, selectedVariantSize);
    setDummyCartItems({
      ...{ paymentItem: [] },
    });
    if (variant) {
      setFoundMatch(true);
      setProduct(variant);
    } else {
      setFoundMatch(false);
    }
  };

  const handlePickSize = (size) => {
    setSelectedVariantSize(size);
    const variant = findVariant(selectedVariantColor, size);
    setDummyCartItems({
      ...{ paymentItem: [] },
    });
    if (variant) {
      setFoundMatch(true);
      setProduct(variant);
    } else {
      setFoundMatch(false);
    }
  };

  // When initialProduct changes (e.g., user navigates to a new product),
  // update parent + product accordingly.
  useEffect(() => {
    if (!initialProduct) return;

    setParent(initialProduct);
    setProduct(getInitialProduct(initialProduct));

    // Reset variant selections when product changes
    setSelectedVariantColor(
      initialProduct?.isVariant && initialProduct?.node === "CHILD"
        ? initialProduct.variant?.colorName
        : null,
    );

    setSelectedVariantSize(
      initialProduct?.isVariant && initialProduct?.node === "CHILD"
        ? initialProduct.variant?.size
        : null,
    );

    setDummyCartItems({ paymentItem: [] });
    setError("");
    setFoundMatch(true);
  }, [initialProduct]);

  useEffect(() => {
    if (!tokenData?.tokenExpiryDate || !tokenData?.token) return;
    if (parent && parent.productId && parent.node == "PARENT") {
      mutateGetProduct({
        token: tokenData.token,
        productId: parent.productId,
        accessPointId: parent.accessPointId,
      });
    }
  }, [
    parent?.productId,
    parent?.accessPointId,
    tokenData?.token,
    tokenData?.tokenExpiryDate,
  ]);

  useEffect(() => {
    if (!cartKey) {
      setCartItems({ paymentItem: [] });
      setDummyCartItems({ paymentItem: [] });
      setHasUserInteracted(false);
      return;
    }

    setCartItems({
      ...{ paymentItem: [] },
      ...cart,
    });

    setDummyCartItems({ paymentItem: [] });
    setHasUserInteracted(false);
  }, [cartKey]);

  useEffect(() => {
    if (
      parent &&
      parent.isVariant &&
      parent.variants &&
      parent.variants.length > 0 &&
      parent.node == "PARENT"
    ) {
      const getVariant = getVariantData(parent);
      setVariantData(getVariant);
    }
  }, [parent]);

  useEffect(() => {
    if (product && product.isVariant && product.node == "CHILD") {
      const initialColor = product.variant?.colorName ?? null;
      const initialSize = product.variant?.size ?? null;
      setSelectedVariantColor(initialColor);
      setSelectedVariantSize(initialSize);
    }
  }, [variantData, product]);

  useEffect(() => {
    if (parent) {
      setProduct(getInitialProduct(parent));
    }
  }, [parent]);

  const uniqueColors = useMemo(() => {
    return [
      ...new Map(
        variantData
          .filter((v) => v.colorName && v.colorCode)
          .map((v) => [
            v.colorCode,
            { colorName: v.colorName, colorCode: v.colorCode },
          ]),
      ).values(),
    ];
  }, [variantData]);

  const uniqueSizes = useMemo(() => {
    return [...new Set(variantData.map((v) => v.size).filter(Boolean))];
  }, [variantData]);

  const invalidateCollections = () => {
    try {
      if (productloggedInUser?.user?.userId) {
        queryClient.invalidateQueries({
          queryKey: [
            "userCollections",
            productloggedInUser?.user?.userId,
            productloggedInUser?.token,
          ],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["guestCollections", invalidateToken],
        });
      }
    } catch (e) {
      console.log("invalidateCollections error", e);
    }
  };

  const { isLoading, mutate: mututateUpdateCart } = useUpdateCart({
    onError: (response) => {
      setError(response.message);
    },
    onSuccess: async (response) => {
      const cart = response.data.collection;
      dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
      invalidateCollections();
      toast.success("Added to Cart");
    },
  });
  const selectedColourName = useMemo(() => {
    return uniqueColors.find(
      (color) => color.colorName === selectedVariantColor,
    );
  }, [uniqueColors, selectedVariantColor]);

  const isInCart = cartItems.paymentItem?.some(
    (item) => item.productId === product?.productId,
  );

  const isDummyInCart = dummyCartItems.paymentItem?.some(
    (item) => item.productId === product?.productId,
  );

  const productInCart = cartItems.paymentItem?.find(
    (item) => item.productId === product?.productId,
  );

  const productInDummyCart = dummyCartItems.paymentItem?.find(
    (item) => item.productId === product?.productId,
  );

  // useEffect(() => {
  //   if (initialCartUpdate && cart?.profileId && cart?.accessPointId) {
  //     setLoadingDummy(false);
  //     mututateUpdateCart({
  //       orgId: cart.profileId,
  //       accessPointId: cart.accessPointId,
  //       paymentItem: debouncedCartItems.paymentItem,
  //     });
  //   }
  //   setInitialCartUpdate(true);
  // }, [debouncedCartItems]);

  useEffect(() => {
    if (!initialCartUpdate) {
      setInitialCartUpdate(true);
      return;
    }

    if (!hasUserInteracted) return;

    if (!cart?.profileId || !cart?.accessPointId) return;

    // 🚫 NOTHING TO SYNC
    if (
      debouncedCartItems.paymentItem.length === 0 &&
      dummyCartItems.paymentItem.length === 0
    ) {
      return;
    }

    setLoadingDummy(false);
    mututateUpdateCart({
      orgId: cart.profileId,
      accessPointId: cart.accessPointId,
      paymentItem: debouncedCartItems.paymentItem,
      cartSource: cartSource,
    });
  }, [debouncedCartItems]);

  const getProductMedia = (media) => {
    if (Array.isArray(media) && media.length > 0) {
      const {
        mediaType,
        mediaThumb,
        mediaUrl,
        mediaExt,
        mediaHeight,
        mediaWidth,
        mediaName,
        mediaTime,
      } = media[0];

      return {
        mediaType,
        mediaThumb,
        mediaUrl,
        mediaExt,
        mediaHeight,
        mediaWidth,
        mediaName,
        mediaTime,
      };
    }

    return {
      mediaType: "image",
      mediaThumb: "",
      mediaUrl: "",
      mediaExt: ".jpg",
      mediaHeight: "",
      mediaWidth: "",
      mediaName: "",
      mediaTime: new Date().toISOString(),
    };
  };

  const getCartPaymentItem = (unit, media, product) => {
    return {
      ...media,
      amount: productPrice,
      initialAmount: productPrice,
      cost: productPrice || 0,
      unit: unit,
      requestedUnit: 1,
      requestedUpdate: false,
      name: product.name,
      currency: product.currency,
      productId: product.productId,
      productNo: product.productNo,
      productCategoryId: product.productCategoryId || null,
      productCategoryName: product.productCategoryName || null,
      variant: product.variant || undefined,
      parentId: product.parentId || undefined,
      node: product.node,
      isVariant: product.isVariant,
    };
  };

  const addToCart = (unit = 1, destination = "dummy") => {
    if (!product) return;

    setHasUserInteracted(true);
    const media = getProductMedia(product.media);
    const { usePromo, useDiscount, useMarkup } = selectedPricePayload;
    const newItem = {
      ...getCartPaymentItem(unit, media, product),
      usePromo,
      useDiscount,
      useMarkup,
    };
    if (destination === "dummy") {
      setDummyCartItems((prev) => ({
        ...prev,
        paymentItem: [...prev.paymentItem, newItem],
      }));
    } else {
      setCartItems((prev) => ({
        ...prev,
        paymentItem: [...prev.paymentItem, newItem],
      }));
    }
  };

  const sendAddToCart = () => {
    if (!cart) {
      setError("Cart is currently unavailable. Please try again later.");
      return;
    }

    setError("");
    setLoadingDummy(true);
    if (dummyCartItems.paymentItem.length == 0) {
      addToCart(1, "real");
    } else {
      setCartItems((prev) => ({
        ...prev,
        paymentItem: [
          ...prev.paymentItem, // Existing items in cartItems
          ...dummyCartItems.paymentItem, // Items from dummyCartItems
        ],
      }));
    }
  };

  const increaseQuantity = () => {
    if (!product) return;
    setHasUserInteracted(true);
    setError("");
    if (isInCart) {
      setCartItems((prevCart) => ({
        ...prevCart,
        paymentItem: prevCart.paymentItem.map((item) =>
          item.productId === product.productId &&
          (product.unlimited || item.unit < product.sku)
            ? { ...item, unit: item.unit + 1 }
            : item,
        ),
      }));
    } else {
      if (isDummyInCart) {
        setDummyCartItems((prevCart) => ({
          ...prevCart,
          paymentItem: prevCart.paymentItem.map((item) =>
            item.productId === product.productId &&
            (product.unlimited || item.unit < product.sku)
              ? { ...item, unit: item.unit + 1 }
              : item,
          ),
        }));
      } else {
        if (product.unlimited || product.sku > 0) {
          const qtyToAdd = product.unlimited ? 2 : Math.min(2, product.sku);
          addToCart(qtyToAdd, "dummy");
        } else {
          setError("No stock available.");
        }
      }
    }
  };

  const decreaseQuantity = () => {
    if (!product) return;
    setHasUserInteracted(true);
    setError("");
    if (isInCart) {
      setCartItems((prevCart) => ({
        ...prevCart,
        paymentItem: prevCart.paymentItem
          .map((item) =>
            item.productId === product.productId && item.unit > 1
              ? { ...item, unit: item.unit - 1 }
              : item,
          )
          .filter((item) => item.unit > 0),
      }));
    } else {
      setDummyCartItems((prevCart) => ({
        ...prevCart,
        paymentItem: prevCart.paymentItem
          .map((item) =>
            item.productId === product.productId && item.unit > 1
              ? { ...item, unit: item.unit - 1 }
              : item,
          )
          .filter((item) => item.unit > 0),
      }));
    }
  };

  function hexToRgba(hex) {
    if (!hex) return null;

    if (hex.length === 8) {
      // AARRGGBB format
      const alpha = parseInt(hex.slice(0, 2), 16) / 255;
      const red = parseInt(hex.slice(2, 4), 16);
      const green = parseInt(hex.slice(4, 6), 16);
      const blue = parseInt(hex.slice(6, 8), 16);

      return `rgba(${red}, ${green}, ${blue}, ${alpha.toFixed(2)})`;
    } else if (hex.length === 6) {
      // RRGGBB format (no alpha)
      const red = parseInt(hex.slice(0, 2), 16);
      const green = parseInt(hex.slice(2, 4), 16);
      const blue = parseInt(hex.slice(4, 6), 16);

      return `rgba(${red}, ${green}, ${blue}, 1)`;
    } else {
      return null;
    }
  }

  const QuantityControls = ({
    unit,
    onIncrease,
    onDecrease,
    disabled = false,
  }) => (
    <div className="quantity-controls white">
      <button onClick={onDecrease} disabled={disabled}>
        -
      </button>
      <span>{unit}</span>
      <button onClick={onIncrease} disabled={disabled}>
        +
      </button>
    </div>
  );

  const AddToCartButton = ({ onClick, isLoading }) => (
    <button
      className={styles.buyButton}
      onClick={onClick}
      disabled={
        isLoading ||
        isLoadingProduct ||
        isLoadingDummy ||
        !getProductFromApi ||
        !foundMatch
      }
    >
      {isLoading || isLoadingDummy ? "Adding to Cart..." : "Add to Cart"}
    </button>
  );

  const ViewCartButton = ({ onClick, isLoading }) => (
    <button
      className={styles.buyButton}
      onClick={onClick}
      disabled={isLoading || isLoadingProduct || isLoadingDummy}
    >
      {isLoading || isLoadingDummy ? "Updating Cart..." : "View Cart"}
    </button>
  );
  if (!parent) return <div>We will get back to you soon.</div>;

  return (
    <>
      <div className={styles.item}>
        {
          <ImageGallery
            media={product?.media?.length > 0 ? product?.media : parent?.media}
            enablePreview={enablePreview}
          />
        }

        <div className={styles.content}>
          <div>
            <div className={styles.address}>
              <strong>Address: </strong>
              {accessPoint.address ||
                organisation.address ||
                "No address available."}
            </div>
            <div className={styles.title}>{product.name}</div>
            <div className={styles.amount}>
              {disableStoreProductPrice
                ? "Contact Seller for Price"
                : formatCurrency(productPrice, product.currency)}
            </div>
            <div className={styles.description}>
              {product?.description || parent.description}
            </div>
          </div>
          {(uniqueColors.length > 0 || uniqueSizes.length > 0) && (
            <div className="variant-container">
              {uniqueColors.length > 0 && (
                <div className="variant-color">
                  <div className="variant-label">
                    Colors: {selectedColourName?.colorName}
                  </div>
                  <div className="color-options">
                    {uniqueColors.map((color, index) => {
                      const isSelected =
                        color.colorName === selectedVariantColor;
                      return (
                        <div
                          key={index}
                          className={`color-div ${
                            isSelected ? "selected" : ""
                          }`}
                          onClick={() => handlePickColour(color.colorName)}
                        >
                          <div
                            className="color-box"
                            style={{
                              backgroundColor: hexToRgba(
                                color.colorCode.startsWith("#")
                                  ? color.colorCode.slice(1)
                                  : color.colorCode,
                              ),
                            }}
                          ></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {uniqueSizes.length > 0 && (
                <div className="variant-size">
                  <div className="variant-label">Sizes:</div>
                  <div className="size-options">
                    {uniqueSizes.map((size, index) => {
                      const isSelected = selectedVariantSize === size;
                      return (
                        <div
                          key={index}
                          className={`size-box ${isSelected ? "selected" : ""}`}
                          onClick={() => handlePickSize(size)}
                        >
                          {size}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          {error && <div className="error-message">{error}</div>}
          {product && isInCart && (
            <>
              <div>
                <div className={styles.cartButton}>
                  <QuantityControls
                    unit={productInCart?.unit || 1}
                    onIncrease={increaseQuantity}
                    onDecrease={decreaseQuantity}
                    disabled={
                      (!product.unlimited && product.sku <= 0) ||
                      !(getProductFromApi && foundMatch)
                    }
                  />
                  <ViewCartButton onClick={showCart} isLoading={isLoading} />
                </div>

                {product && !(product.unlimited || product.sku > 0) && (
                  <div className={styles.outOfStockMessage}>
                    This item in your cart is currently out of stock. Please
                    remove it from the cart.
                  </div>
                )}
              </div>
            </>
          )}
          {product && !isInCart && (
            <div className={styles.cartButton}>
              {!foundMatch ? (
                <>
                  <div className={styles.outOfStockMessage}>
                    {selectedVariantColor && selectedVariantSize ? (
                      <>
                        No match found for this color{" "}
                        <span className={styles.highlight}>
                          {selectedVariantColor}
                        </span>{" "}
                        and size{" "}
                        <span className={styles.highlight}>
                          {selectedVariantSize}
                        </span>
                        .
                        <div className={styles.suggestion}>
                          Try choosing a different color or size.
                        </div>
                      </>
                    ) : selectedVariantColor ? (
                      <>
                        No match found for this color{" "}
                        <span className={styles.highlight}>
                          {selectedVariantColor}
                        </span>
                        .
                        <div className={styles.suggestion}>
                          Try another color option.
                        </div>
                      </>
                    ) : selectedVariantSize ? (
                      <>
                        No match found for this size{" "}
                        <span className={styles.highlight}>
                          {selectedVariantSize}
                        </span>
                        .
                        <div className={styles.suggestion}>
                          Try another size option.
                        </div>
                      </>
                    ) : (
                      "Please select a color and size"
                    )}
                  </div>
                </>
              ) : (
                <>
                  {product.unlimited || product.sku > 0 ? (
                    <>
                      <QuantityControls
                        unit={productInDummyCart?.unit || 1}
                        onIncrease={increaseQuantity}
                        onDecrease={decreaseQuantity}
                        disabled={!(getProductFromApi && foundMatch)}
                      />
                      <AddToCartButton
                        onClick={sendAddToCart}
                        isLoading={isLoading}
                      />
                    </>
                  ) : (
                    <>
                      <div className={styles.outOfStockMessage}>
                        This item is currently out of stock
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          <div>
            <button className={styles.messageButton} onClick={onMessage}>
              Chat store admin
            </button>
          </div>
        </div>
      </div>

      {openChatWindow && (
        <ChatWindow
          loggedInUser={loggedInUser}
          isChatOpen={openChatWindow}
          closeChat={closeChat}
          chatPayload={chatPayload}
          chatInfo={chatInfo}
          markCommentsAsRead={markCommentsAsRead}
          presetMessage={presetMessage}
          clearPresetMessage={clearPresetMessage}
          activateGuestMode={activateGuestMode}
          chatGuestMode={chatGuestMode}
          chatBoxFetchError={chatBoxFetchError}
          handleBoxRetry={handleBoxRetry}
          showSenderName={showSenderName}
        />
      )}
    </>
  );
};

export default ProductDetails;
